/**
 * Asking Sentinel things, and letting it act on the answer.
 *
 * One thread rather than a dialog that forgets. A mission is a conversation
 * held over an hour by people who hand over halfway, so the questions belong
 * with the mission and in the report beside what was happening when they were
 * asked. The server stores them; this only draws them.
 *
 * Live narration is a toggle on the composer, not a mode. When it is on, the
 * run's read appears in the thread as another voice and the placeholder says so
 * — the point being that an operator watching the picture change and an
 * operator asking a question are the same person doing the same job, and
 * putting those in two places means one of them goes unread.
 *
 * Nothing here applies a route. Options are drawn as cards with their costs and
 * a person clicks one, for the reason the routing engine gives: moving a
 * protection detail onto a road no one has looked at is least safe to do
 * silently at exactly the moment it feels most urgent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import SendIcon from '@mui/icons-material/Send';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { RichTooltip } from '../../components/common';
import {
  Mission,
  MissionChatTurn,
  MissionRouteStatus,
  RouteOption,
  SENTINEL_ACTION_CAVEAT,
  SENTINEL_ACTION_HINTS,
  SENTINEL_ACTION_KINDS,
  SENTINEL_ACTION_LABELS,
  SentinelAction,
  SentinelFollower,
  applyRoute,
  askMissionSentinel,
  clearMissionChat,
  getMissionChat,
  runSentinelAction,
  updateMission,
} from '../../services/missionApi';
import {
  CHAT_SPEAKER,
  PENDING_STEPS,
  followerFact,
  isRecommended,
  isStaleRejoin,
  optionKey,
  optionLabel,
  optionStats,
  pendingStepIndex,
  suggestedAsks,
} from './chatView';
import { buildAlertFeed } from './alertWaterfall';
import { formatWhen } from './format';
import { RouteOptionPreview } from './map/MissionOptionPreviewLayer';
import { metalSx } from '../../theme/metalButtons';

const GOLD = '#f5b942';
const TEAL = '#00b8a9';
const MUTED = '#8f9bb3';
const RED = '#ef5350';
const PURPLE = '#9b7fe8';

const ATTENTION_TONE: Record<string, string> = {
  none: MUTED,
  watch: GOLD,
  act: RED,
};

const CHIP_DOT: Record<SentinelAction, string> = {
  detection_route: TEAL,
  route_around_threats: GOLD,
  avoid_watchlist: PURPLE,
  rejoin_route: RED,
};

const ACTIONS: SentinelAction[] = [
  'detection_route',
  'route_around_threats',
  'avoid_watchlist',
];

/**
 * Buttons that only mean something in a particular state.
 *
 * A correction is the clearest case: with the convoy on the line there is
 * nothing to correct, and a permanent button for it is a fourth thing to read
 * past on every mission that is going fine. When the convoy does stray it
 * appears highlighted, which also serves as the prompt — the operator watching
 * the map does not have to remember that the option exists.
 */
const CONTEXTUAL: SentinelAction[] = ['rejoin_route'];

const RAIL: Record<string, string> = {
  user: '#5b6570',
  sentinel: GOLD,
  system: TEAL,
};

const OptionCard: React.FC<{
  option: RouteOption;
  kind?: SentinelAction;
  recommended: boolean;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
  onPreview: (option: RouteOption | null) => void;
}> = ({ option, kind, recommended, busy, onApply, onDismiss, onPreview }) => (
  <Box
    onMouseEnter={() => onPreview(option)}
    onMouseLeave={() => onPreview(null)}
    sx={{
      mt: 0.75,
      p: 1,
      borderRadius: 1,
      border: '1px solid',
      borderColor: recommended ? 'rgba(0,184,169,0.5)' : 'rgba(255,255,255,0.12)',
      bgcolor: '#171a1f',
      boxShadow: recommended ? '0 0 0 1px rgba(0,184,169,0.15)' : 'none',
      '&:hover': { borderColor: TEAL },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Typography
        variant="caption"
        noWrap
        sx={{ fontWeight: 700, fontSize: '0.72rem', flex: 1, minWidth: 0, color: '#e8ebee' }}
      >
        {optionLabel(option)}
      </Typography>
      {recommended && (
        <Chip
          size="small"
          label="Sentinel's pick"
          sx={{
            height: 16, fontSize: '0.52rem', fontWeight: 700, letterSpacing: 0.4,
            color: TEAL, border: `1px solid ${TEAL}`, bgcolor: 'transparent',
            textTransform: 'uppercase',
            '& .MuiChip-label': { px: 0.55 },
          }}
        />
      )}
      <RichTooltip
        eyebrow="Applies"
        body="Changes the mission route to this one."
        hint="The only control here that moves the convoy"
      >
        <span>
          <Box
            component="button"
            type="button"
            disabled={busy}
            onClick={busy ? undefined : onApply}
            sx={{
              ...metalSx('steel'),
              height: 22,
              px: 1.1,
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: 'inherit',
              fontWeight: 800,
              fontSize: '0.62rem',
              letterSpacing: 0.2,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Take it
          </Box>
        </span>
      </RichTooltip>
      <RichTooltip
        eyebrow="Dismiss"
        body="Puts this offer away. The convoy keeps the route it is on."
        hint="Use this when the line is already held, or the option is no longer wanted"
      >
        <span>
          <Box
            component="button"
            type="button"
            disabled={busy}
            onClick={busy ? undefined : onDismiss}
            sx={{
              height: 22,
              px: 0.9,
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: 'inherit',
              fontWeight: 700,
              fontSize: '0.62rem',
              letterSpacing: 0.2,
              color: MUTED,
              bgcolor: 'transparent',
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 0.75,
              cursor: busy ? 'default' : 'pointer',
              '&:hover': busy ? undefined : { color: '#e8ebee', borderColor: 'rgba(255,255,255,0.28)' },
            }}
          >
            Dismiss
          </Box>
        </span>
      </RichTooltip>
    </Stack>

    <Stack direction="row" spacing={0.5} sx={{ mt: 0.7, flexWrap: 'wrap', gap: 0.5 }}>
      {optionStats(option, kind).map((stat) => (
        <Box
          key={`${stat.label}-${stat.value}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.4,
            px: 0.7,
            py: 0.15,
            borderRadius: 0.5,
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
            fontSize: '0.58rem',
            color: MUTED,
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: stat.tone === 'good' ? TEAL : stat.tone === 'bad' ? '#ff9d97' : '#e8ebee',
            }}
          >
            {stat.value}
          </Box>
          {stat.label}
        </Box>
      ))}
    </Stack>
  </Box>
);

const FollowerFacts: React.FC<{ followers: SentinelFollower[] }> = ({ followers }) => (
  <Stack spacing={0.4} sx={{ mt: 0.7 }}>
    {followers.slice(0, 4).map((row, index) => {
      const fact = followerFact(row);
      return (
        <Box
          key={`${fact.label}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            px: 0.85,
            py: 0.55,
            borderRadius: 0.75,
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: '#171a1f',
            fontSize: '0.62rem',
            color: '#c6cdd5',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: '#e8ebee' }}>{fact.label}</Box>
          {fact.state && (
            <Box
              component="span"
              sx={{
                fontSize: '0.52rem',
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: '#ff9d97',
                bgcolor: 'rgba(239,83,80,0.12)',
                border: '1px solid #5c2b29',
                px: 0.6,
                borderRadius: 1,
              }}
            >
              {fact.watchlisted ? `${fact.state} · watchlisted` : fact.state}
            </Box>
          )}
          {fact.bits.map((bit) => (
            <React.Fragment key={bit}>
              <Box component="span" sx={{ color: '#5b6570' }}>·</Box>
              <Box component="span">{bit}</Box>
            </React.Fragment>
          ))}
        </Box>
      );
    })}
  </Stack>
);

const Turn: React.FC<{
  turn: MissionChatTurn;
  busy: boolean;
  options: RouteOption[];
  onApply: (option: RouteOption) => void;
  onDismiss: (option: RouteOption) => void;
  onPreview: (preview: RouteOptionPreview | null) => void;
}> = ({ turn, busy, options, onApply, onDismiss, onPreview }) => {
  const mine = turn.role === 'user';
  const live = turn.role === 'system';
  const followers = turn.payload?.followers || [];
  const showFacts = turn.role === 'sentinel' && (turn.payload?.intent === 'follow' || followers.length > 0)
    && followers.length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 0.35,
        pl: 1.4,
        pr: 0.75,
        py: 0.7,
        borderRadius: 1,
        bgcolor: turn.role === 'sentinel' ? 'rgba(255,255,255,0.03)' : 'transparent',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 8,
          bottom: 8,
          width: 2,
          borderRadius: 1,
          bgcolor: RAIL[turn.role] || '#333a45',
        },
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={0.7}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            fontSize: '0.55rem',
            letterSpacing: 1.2,
            color: mine ? MUTED : (live ? TEAL : GOLD),
          }}
        >
          {CHAT_SPEAKER[turn.role] || turn.role.toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.52rem', color: '#5b6570' }}>
          {formatWhen(turn.created_at)}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: '0.7rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          color: mine ? '#c6cdd5' : '#c6cdd5',
        }}
      >
        {turn.body}
      </Typography>

      {showFacts && <FollowerFacts followers={followers} />}

      {options.map((option, i) => (
        <OptionCard
          key={`${turn.id}-${option.label || i}`}
          option={option}
          kind={turn.payload?.kind}
          recommended={isRecommended(option, turn.payload?.recommend)}
          busy={busy}
          onApply={() => onApply(option)}
          onDismiss={() => onDismiss(option)}
          onPreview={(focus) => onPreview(focus
            ? { options, focus, recommend: turn.payload?.recommend }
            : null)}
        />
      ))}
    </Box>
  );
};

/**
 * The question, shown before the answer exists.
 *
 * A detection route is half a dozen routing calls and a model on top, so the
 * gap between pressing and hearing back is long enough to be mistaken for a
 * dead button — and pressing again is expensive, because the second one is a
 * real second reroute. The checklist is known before the HTTP call; ticking
 * it on a timer is honest enough without a new channel.
 */
const PendingTurn: React.FC<{ body: string }> = ({ body }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Date.now() - started), 400);
    return () => window.clearInterval(timer);
  }, []);
  const current = pendingStepIndex(elapsed);

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 0.35,
        pl: 1.4,
        pr: 0.75,
        py: 0.7,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 8,
          bottom: 8,
          width: 2,
          borderRadius: 1,
          bgcolor: '#5b6570',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, fontSize: '0.55rem', letterSpacing: 1.2, color: MUTED }}
      >
        {CHAT_SPEAKER.user}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.5, color: '#c6cdd5' }}
      >
        {body}
      </Typography>
      <Stack spacing={0.35} sx={{ mt: 0.6 }}>
        {PENDING_STEPS.map((step, index) => {
          const done = index < current;
          const now = index === current;
          return (
            <Stack key={step} direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  border: '1.5px solid',
                  borderColor: done ? TEAL : now ? GOLD : 'rgba(255,255,255,0.16)',
                  display: 'grid',
                  placeItems: 'center',
                  '@keyframes sentinelStep': {
                    '0%,100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                  },
                  animation: now ? 'sentinelStep 1.2s ease-in-out infinite' : 'none',
                }}
              >
                {done && (
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: TEAL }} />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: done ? MUTED : now ? GOLD : '#5b6570',
                }}
              >
                {step}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export interface MissionSentinelChatProps {
  mission: Mission;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
  /** Bumped by the host when the monitor ticks, to pick up live narration. */
  refreshKey?: number;
  /** Whether anything has strayed, which unlocks the correction button. */
  offRoute?: boolean;
  status?: MissionRouteStatus | null;
  onPreviewOption?: (preview: RouteOptionPreview | null) => void;
  fill?: boolean;
}

const MissionSentinelChat: React.FC<MissionSentinelChatProps> = ({
  mission, onError, onMissionChange, refreshKey, offRoute, status, onPreviewOption, fill,
}) => {
  const [turns, setTurns] = useState<MissionChatTurn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  /** What we have asked but not yet heard back about. */
  const [pending, setPending] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const endRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef(onError);
  errorRef.current = onError;
  const busyRef = useRef(false);
  busyRef.current = !!busy;
  const previewRef = useRef(onPreviewOption);
  previewRef.current = onPreviewOption;
  /**
   * Guards the thread against its own refetches.
   *
   * An action is slow — several routing calls and a model — and the monitor
   * ticks while it runs, so a refetch is usually in flight when the answer
   * lands. Without this, a response that predates the answer arrives after it
   * and overwrites the thread, and the reply appears and vanishes before
   * anyone reads it. Any mutation invalidates the fetches that came before it.
   */
  const loadSeq = useRef(0);

  const liveOn = !!mission.sentinel_live_enabled;
  const liveRead = mission.current_run?.live_read || null;
  const actions = offRoute ? [...CONTEXTUAL, ...ACTIONS] : ACTIONS;

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const fetched = await getMissionChat(mission.id);
      if (seq === loadSeq.current) setTurns(fetched);
    } catch {
      if (seq === loadSeq.current) setTurns([]);
    }
  }, [mission.id]);

  const append = useCallback((added: MissionChatTurn[]) => {
    loadSeq.current += 1;
    setTurns((current) => [...current, ...(added || [])]);
  }, []);

  useEffect(() => {
    // Refetching mid-answer can only race the answer, and the action appends
    // whatever it produced anyway.
    if (busyRef.current) return;
    load();
  }, [load, refreshKey]);

  useEffect(() => () => { previewRef.current?.(null); }, []);

  useEffect(() => {
    setDismissed(new Set());
  }, [mission.id]);

  // A correction that is still on screen after the convoy found the line
  // is a decision that no longer exists. Retire it so Take it cannot move
  // them onto a detour they no longer need.
  useEffect(() => {
    if (offRoute) return;
    const stale = turns.flatMap((turn) => (
      isStaleRejoin(turn.payload?.kind, false)
        ? (turn.payload?.options || []).map((option) => optionKey(turn.id, option))
        : []
    ));
    const fresh = stale.filter((key) => !dismissed.has(key));
    if (fresh.length === 0) return;
    setDismissed((prev) => {
      const next = new Set(prev);
      fresh.forEach((key) => next.add(key));
      return next;
    });
    previewRef.current?.(null);
  }, [dismissed, offRoute, turns]);

  const offeredOn = (turn: MissionChatTurn): RouteOption[] => {
    if (isStaleRejoin(turn.payload?.kind, offRoute)) return [];
    return (turn.payload?.options || []).filter(
      (option) => !dismissed.has(optionKey(turn.id, option)),
    );
  };

  // Only when something arrives, so reading back through the thread is not
  // interrupted by the panel scrolling itself on every parent render.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [turns.length, pending]);

  const guard = useCallback(async (key: string, work: () => Promise<void>) => {
    setBusy(key);
    errorRef.current(null);
    try {
      await work();
    } catch (e: any) {
      errorRef.current(e?.response?.data?.detail || e?.message || 'Sentinel could not do that');
    } finally {
      setBusy(null);
      setPending(null);
    }
  }, []);

  const ask = (text = question.trim()) => {
    if (!text) return;
    setQuestion('');
    setPending(text);
    guard('ask', async () => {
      const { turns: added } = await askMissionSentinel(mission.id, text);
      append(added);
    });
  };

  const act = (action: SentinelAction) => {
    // Echoed into the thread straight away. The server writes the real prompt
    // turn, but it does not commit until the whole action is done, and half a
    // minute of a silent panel reads as a button that did nothing.
    setPending(SENTINEL_ACTION_LABELS[action]);
    return guard(action, async () => {
      const { turns: added } = await runSentinelAction(mission.id, action);
      append(added);
    });
  };

  const clear = () => guard('clear', async () => {
    await clearMissionChat(mission.id);
    setTurns([]);
  });

  const toggleLive = () => guard('live', async () => {
    await updateMission(mission.id, { sentinel_live_enabled: !liveOn });
    onMissionChange();
  });

  const apply = (option: RouteOption) => guard('apply', async () => {
    const cleared = (option.clears || []).map((c) => c.event_id);
    await applyRoute(mission.id, option, cleared, option.label || undefined);
    previewRef.current?.(null);
    await load();
    onMissionChange();
  });

  const dismiss = (turn: MissionChatTurn, option: RouteOption) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(optionKey(turn.id, option));
      return next;
    });
    previewRef.current?.(null);
  };

  const suggestions = useMemo(() => {
    const labels: string[] = [];
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const rows = turns[i].payload?.followers || [];
      for (const row of rows) {
        if (row.label && !labels.includes(row.label)) labels.push(row.label);
      }
      if (labels.length) break;
    }
    const openAlerts = buildAlertFeed(status?.events || [], () => '').openCount;
    const hasRehearsal = (mission.runs || []).some((run) => run.kind === 'rehearsal')
      || mission.current_run?.kind === 'rehearsal';
    return suggestedAsks({
      followerLabels: labels,
      openAlerts,
      hasRehearsal,
      inProgress: mission.status === 'in_progress',
    });
  }, [turns, status, mission.runs, mission.current_run?.kind, mission.status]);

  return (
    <Box sx={{
      mt: fill ? 0.5 : 1,
      pt: 0.75,
      borderTop: '1px solid rgba(255,255,255,0.08)',
      ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
    }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3, flex: 1 }}>
          ASK SENTINEL
        </Typography>
        {turns.length > 0 && (
          <Tooltip title="Clear this conversation">
            <IconButton size="small" onClick={clear} disabled={!!busy} sx={{ p: 0.25 }}>
              <DeleteSweepIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
        {actions.map((action) => {
          const urgent = action === 'rejoin_route';
          const pressed = busy === action;
          return (
            <RichTooltip
              key={action}
              eyebrow={SENTINEL_ACTION_KINDS[action]}
              eyebrowTone={urgent ? RED : undefined}
              body={SENTINEL_ACTION_HINTS[action]}
              hint={SENTINEL_ACTION_CAVEAT}
            >
              <Chip
                size="small"
                disabled={!!busy}
                icon={(
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: urgent ? '50%' : '2px',
                      bgcolor: CHIP_DOT[action],
                      ml: '8px !important',
                      '@keyframes sentinelUrgent': {
                        '0%,100%': { opacity: 1 },
                        '50%': { opacity: 0.35 },
                      },
                      animation: urgent ? 'sentinelUrgent 1.6s ease-in-out infinite' : 'none',
                    }}
                  />
                )}
                label={pressed ? 'Working…' : SENTINEL_ACTION_LABELS[action]}
                onClick={busy ? undefined : () => act(action)}
                sx={{
                  height: 22,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  cursor: busy ? 'default' : 'pointer',
                  color: urgent ? '#ff9d97' : undefined,
                  bgcolor: urgent ? 'rgba(239,83,80,0.12)' : 'rgba(255,255,255,0.05)',
                  border: urgent ? '1px solid #5c2b29' : '1px solid rgba(255,255,255,0.08)',
                  opacity: busy && !pressed ? 0.55 : 1,
                  '&:hover': busy
                    ? undefined
                    : { bgcolor: urgent ? 'rgba(239,83,80,0.26)' : 'rgba(245,185,66,0.16)' },
                  '& .MuiChip-label': { px: 0.7 },
                }}
              />
            </RichTooltip>
          );
        })}
      </Stack>

      {!pending && suggestions.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.45 }}>
          {suggestions.map((chip) => (
            <Chip
              key={chip}
              size="small"
              label={chip}
              onClick={() => (busy ? undefined : ask(chip))}
              sx={{
                height: 18,
                fontSize: '0.55rem',
                fontWeight: 600,
                color: MUTED,
                bgcolor: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                cursor: busy ? 'default' : 'pointer',
                '& .MuiChip-label': { px: 0.7 },
              }}
            />
          ))}
        </Stack>
      )}

      <Box sx={{
        flex: fill ? 1 : undefined,
        minHeight: fill ? 0 : undefined,
        maxHeight: fill ? undefined : 320,
        overflow: 'auto',
        mb: 0.75,
        pr: 0.5,
        order: fill ? 3 : undefined,
      }}>
        {turns.map((turn) => (
          <Turn
            key={turn.id}
            turn={turn}
            busy={!!busy}
            options={offeredOn(turn)}
            onApply={apply}
            onDismiss={(option) => dismiss(turn, option)}
            onPreview={(preview) => onPreviewOption?.(preview)}
          />
        ))}
        {pending && <PendingTurn body={pending} />}
        {liveOn && liveRead && (
          <Box
            sx={{
              mt: 0.5,
              p: 0.75,
              borderRadius: 0.5,
              borderLeft: `3px solid ${ATTENTION_TONE[liveRead.attention] || MUTED}`,
              bgcolor: 'rgba(255,255,255,0.03)',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.67rem', lineHeight: 1.45 }}>
              {liveRead.read}
            </Typography>
            {mission.current_run?.live_read_at && (
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.57rem', color: 'text.disabled' }}>
                {formatWhen(mission.current_run.live_read_at)}
              </Typography>
            )}
          </Box>
        )}
        {liveOn && !liveRead && turns.length === 0 && !pending && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.4, fontSize: '0.62rem' }}
          >
            Waiting for something to change. An unchanged picture is not narrated twice.
          </Typography>
        )}
        {!liveOn && turns.length === 0 && !pending && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.4, fontSize: '0.62rem', lineHeight: 1.4 }}
          >
            Ask whether you are being followed for the assessment and a recommended
            evade-or-test. The buttons above also ask the routing engine for real
            options; none of them moves the convoy until you take one.
          </Typography>
        )}
        <div ref={endRef} />
      </Box>

      <Box sx={{ position: 'relative', mb: 0.75, order: fill ? 4 : undefined, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder={liveOn ? '' : 'Ask about this mission'}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
          }}
          InputProps={{
            sx: { fontSize: '0.7rem', pr: 5.5 },
          }}
        />

        {/* The transparent LIVE watermark: present enough to say the run is
            being narrated, faint enough to type over. */}
        {liveOn && !question && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: 11,
              top: 8,
              pointerEvents: 'none',
              fontSize: '0.7rem',
              color: TEAL,
              opacity: 0.45,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            LIVE updates on · ask anything
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={0.25}
          sx={{ position: 'absolute', right: 6, top: 5, alignItems: 'center' }}
        >
          <RichTooltip
            eyebrow="Live narration"
            eyebrowTone={liveOn ? TEAL : undefined}
            body={liveOn
              ? 'Stops Sentinel narrating the run. Questions and reports still work.'
              : 'Sentinel narrates the run in this thread as it goes.'}
            hint="Only when the picture changes, and at most once every few minutes"
          >
            <IconButton
              size="small"
              onClick={toggleLive}
              disabled={busy === 'live'}
              sx={{ p: 0.25, color: liveOn ? TEAL : 'text.disabled' }}
            >
              <SensorsIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </RichTooltip>
          {busy === 'ask' ? (
            <CircularProgress size={13} sx={{ color: GOLD, mx: 0.5 }} />
          ) : (
            <RichTooltip
              eyebrow="Ask"
              body="Puts the question to Sentinel, answered from the mission brief."
              keys={['Enter']}
              hint="sends · Shift + Enter for a new line"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => ask()}
                  disabled={!question.trim() || !!busy}
                  sx={{ p: 0.25, color: question.trim() ? GOLD : 'text.disabled' }}
                >
                  <SendIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </RichTooltip>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default MissionSentinelChat;
